import { useState, useMemo, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { campaignsDataAtom } from '@/store';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, Pin, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CAMPAIGN_TYPE, getCampaignType } from '@/lib/types';
import { fetchPerformanceReport } from '@/lib/apis';
import type { PerformanceData } from '@/lib/types';

// Chart typography constants
const CHART_FONT = {
  AXIS_TICK: 11,
  AXIS_LABEL: 13,
  LEGEND: 11,
} as const;

// Metric colors
const METRIC_COLORS: Record<string, string> = {
  spend: '#290BD9',
  impressions: '#059669',
  clicks: '#0891b2',
  conversions: '#7c3aed',
  impressionShare: '#dc2626',
  cpc: '#ea580c',
  cpa: '#be185d',
  roas: '#0d9488',
  conversionValue: '#4f46e5',
};

// Chart data point after transforming PerformanceData
interface ChartDataPoint {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  cpc: number;
  cpa: number;
  roas: number;
  conversionValue: number;
  impressionShare: number;
}

// Transform raw PerformanceData to chart format
function transformPerformanceData(data: PerformanceData[]): ChartDataPoint[] {
  return data.map((d) => {
    const cost = d.cost || 0;
    const clicks = d.clicks || 0;
    const conversions = d.conversions || 0;
    const conversionValue = d.all_conversions_value || 0;

    return {
      date: d.date,
      spend: Math.round(cost * 100) / 100,
      impressions: d.impressions || 0,
      clicks,
      conversions,
      cpc: clicks > 0 ? Math.round((cost / clicks) * 100) / 100 : 0,
      cpa: conversions > 0 ? Math.round((cost / conversions) * 100) / 100 : 0,
      roas: cost > 0 ? Math.round((conversionValue / cost) * 100) / 100 : 0,
      conversionValue: Math.round(conversionValue * 100) / 100,
      impressionShare: 0, // Not available from this endpoint
    };
  });
}

// Group daily data points by week/month/year
function groupDataBy(data: ChartDataPoint[], groupBy: 'day' | 'week' | 'month' | 'year'): ChartDataPoint[] {
  if (groupBy === 'day') return data;

  const groups = new Map<string, ChartDataPoint[]>();

  for (const point of data) {
    const date = new Date(point.date);
    let key: string;

    if (groupBy === 'week') {
      // ISO week start (Monday)
      const d = new Date(date);
      d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      key = d.toISOString().split('T')[0];
    } else if (groupBy === 'month') {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
    } else {
      key = `${date.getFullYear()}-01-01`;
    }

    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(point);
  }

  return Array.from(groups.entries()).map(([key, points]) => {
    const totalSpend = points.reduce((s, p) => s + p.spend, 0);
    const totalClicks = points.reduce((s, p) => s + p.clicks, 0);
    const totalConversions = points.reduce((s, p) => s + p.conversions, 0);
    const totalConversionValue = points.reduce((s, p) => s + p.conversionValue, 0);

    return {
      date: key,
      spend: Math.round(totalSpend * 100) / 100,
      impressions: points.reduce((s, p) => s + p.impressions, 0),
      clicks: totalClicks,
      conversions: totalConversions,
      cpc: totalClicks > 0 ? Math.round((totalSpend / totalClicks) * 100) / 100 : 0,
      cpa: totalConversions > 0 ? Math.round((totalSpend / totalConversions) * 100) / 100 : 0,
      roas: totalSpend > 0 ? Math.round((totalConversionValue / totalSpend) * 100) / 100 : 0,
      conversionValue: Math.round(totalConversionValue * 100) / 100,
      impressionShare: 0,
    };
  });
}

// Chart settings interface
interface ChartSettings {
  campaigns: 'all' | 'brand' | 'prospecting' | 'competitor';
  metrics: string[];
  overlays: {
    newCampaigns: boolean;
    optimizations: boolean;
  };
  xAxis: 'day' | 'week' | 'month' | 'year' | 'icp' | 'funnel';
  chartType: 'line' | 'bar' | 'table' | 'pie';
  dateRange: number;
  groupBy: 'day' | 'week' | 'month' | 'year';
  customDateStart: string | null;
  customDateEnd: string | null;
}

const DATE_RANGE_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '365 days' },
  { value: 0, label: 'All time' },
];

const GROUP_BY_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
];

const METRIC_OPTIONS = [
  { value: 'spend', label: 'Spend' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'conversions', label: 'Conversions' },
  { value: 'cpc', label: 'CPC' },
  { value: 'cpa', label: 'CPA' },
  { value: 'roas', label: 'ROAS' },
  { value: 'conversionValue', label: 'Conversion Value' },
];

const CAMPAIGN_FILTER_OPTIONS = [
  { value: 'all', label: 'All Campaigns' },
  { value: 'brand', label: 'Brand Campaigns' },
  { value: 'prospecting', label: 'Prospecting Campaigns' },
  { value: 'competitor', label: 'Competitor Campaigns' },
];

const CHART_TYPE_OPTIONS = [
  { value: 'line', label: 'Line Chart' },
  { value: 'bar', label: 'Bar Chart' },
  { value: 'table', label: 'Table' },
  { value: 'pie', label: 'Pie Chart' },
];

interface AdvancedPerformanceChartProps {
  currencySymbol?: string;
  onControlsReady?: (controls: React.ReactNode) => void;
}

export function AdvancedPerformanceChart({ currencySymbol = '€', onControlsReady }: AdvancedPerformanceChartProps) {
  const campaigns = useAtomValue(campaignsDataAtom);
  const [settings, setSettings] = useState<ChartSettings>({
    campaigns: 'all',
    metrics: ['spend', 'clicks', 'conversions'],
    overlays: {
      newCampaigns: false,
      optimizations: false,
    },
    xAxis: 'day',
    chartType: 'line',
    dateRange: 30,
    groupBy: 'day',
    customDateStart: null,
    customDateEnd: null,
  });

  const [isPinned, setIsPinned] = useState(false);

  // Get campaign IDs based on type filter
  const filteredCampaignIds = useMemo(() => {
    if (!campaigns.length) return '';

    const TYPE_MAP: Record<string, CAMPAIGN_TYPE> = {
      brand: CAMPAIGN_TYPE.BRAND,
      prospecting: CAMPAIGN_TYPE.PROSPECTING,
      competitor: CAMPAIGN_TYPE.COMPETITOR,
    };

    const filtered = settings.campaigns === 'all'
      ? campaigns
      : campaigns.filter((c) => getCampaignType(c) === TYPE_MAP[settings.campaigns]);

    if (filtered.length === 0) return '';
    return filtered.map((c) => c.id).join(',');
  }, [campaigns, settings.campaigns]);

  // Derive query params
  const days = settings.dateRange || 365; // 0 = all time → default to 365
  const dateRangeParam = `${days}d`;
  const timeRange = days <= 90 ? 'daily' : 'weekly';

  // Fetch performance data via React Query
  const { data: rawData = [], isLoading, error: queryError } = useQuery({
    queryKey: ['advanced-performance', filteredCampaignIds, dateRangeParam, timeRange],
    queryFn: ({ signal }) => fetchPerformanceReport(timeRange, dateRangeParam, filteredCampaignIds, signal),
    enabled: !!filteredCampaignIds,
  });

  const error = queryError ? 'Failed to load performance data' : null;

  // Transform and group data
  const chartData = useMemo(() => {
    const transformed = transformPerformanceData(rawData);
    return groupDataBy(transformed, settings.groupBy);
  }, [rawData, settings.groupBy]);

  // Pie chart data aggregation
  const pieData = useMemo(() => {
    if (settings.chartType !== 'pie') return [];
    return settings.metrics.map((metric) => ({
      name: METRIC_OPTIONS.find((m) => m.value === metric)?.label || metric,
      value: chartData.reduce((sum, item) => sum + (item[metric as keyof ChartDataPoint] as number || 0), 0),
      color: METRIC_COLORS[metric],
    }));
  }, [chartData, settings.metrics, settings.chartType]);

  const handleMetricToggle = (metric: string) => {
    setSettings((prev) => ({
      ...prev,
      metrics: prev.metrics.includes(metric)
        ? prev.metrics.filter((m) => m !== metric)
        : [...prev.metrics, metric],
    }));
  };

  const formatDate = (dateStr: string | number | React.ReactNode) => {
    const date = new Date(String(dateStr));
    if (settings.groupBy === 'year') return date.getFullYear().toString();
    if (settings.groupBy === 'month') return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
    if (settings.groupBy === 'week') return `W${Math.ceil(date.getDate() / 7)}`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const formatValue = (value: number, metric: string) => {
    if (['spend', 'cpc', 'cpa', 'conversionValue'].includes(metric)) {
      return `${currencySymbol}${value.toFixed(2)}`;
    }
    if (['roas'].includes(metric)) {
      return `${value.toFixed(2)}x`;
    }
    return value.toLocaleString();
  };

  const renderChart = () => {
    if (isLoading) {
      return <Skeleton className="w-full h-[400px] rounded-lg" />;
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-[400px] text-gray-500">
          {error}
        </div>
      );
    }

    if (chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-[400px] text-gray-500">
          No performance data available for the selected filters
        </div>
      );
    }

    if (settings.chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              fontSize={CHART_FONT.AXIS_TICK}
              tick={{ fill: '#6b7280' }}
            />
            <YAxis fontSize={CHART_FONT.AXIS_TICK} tick={{ fill: '#6b7280' }} />
            <Tooltip
              formatter={(value: unknown, name?: string | number) => [formatValue(Number(value ?? 0), String(name ?? '')), String(name ?? '')]}
              labelFormatter={formatDate}
            />
            <Legend />
            {settings.metrics.map((metric) => (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                name={METRIC_OPTIONS.find((m) => m.value === metric)?.label || metric}
                stroke={METRIC_COLORS[metric]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (settings.chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              fontSize={CHART_FONT.AXIS_TICK}
              tick={{ fill: '#6b7280' }}
            />
            <YAxis fontSize={CHART_FONT.AXIS_TICK} tick={{ fill: '#6b7280' }} />
            <Tooltip
              formatter={(value: unknown, name?: string | number) => [formatValue(Number(value ?? 0), String(name ?? '')), String(name ?? '')]}
              labelFormatter={formatDate}
            />
            <Legend />
            {settings.metrics.map((metric) => (
              <Bar
                key={metric}
                dataKey={metric}
                name={METRIC_OPTIONS.find((m) => m.value === metric)?.label || metric}
                fill={METRIC_COLORS[metric]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (settings.chartType === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={150}
              label={(props: { name?: string; percent?: number }) => `${props.name ?? ''}: ${((props.percent ?? 0) * 100).toFixed(1)}%`}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    // Table view
    return (
      <div className="overflow-x-auto max-h-[400px]">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-white sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              {settings.metrics.map((metric) => (
                <th key={metric} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {METRIC_OPTIONS.find((m) => m.value === metric)?.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {chartData.map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{formatDate(row.date)}</td>
                {settings.metrics.map((metric) => (
                  <td key={metric} className="px-4 py-3 text-sm text-gray-900 text-right">
                    {formatValue(row[metric as keyof ChartDataPoint] as number, metric)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const controlsElement = (
    <>
      {/* Campaigns Popover */}
      <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 text-gray-900">
                {CAMPAIGN_FILTER_OPTIONS.find((c) => c.value === settings.campaigns)?.label}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0">
              {/* Campaign Type Filter - Horizontal Tabs */}
              <div className="p-2">
                <Tabs
                  value={settings.campaigns}
                  onValueChange={(value) => setSettings((prev) => ({ ...prev, campaigns: value as ChartSettings['campaigns'] }))}
                >
                  <TabsList className="grid grid-cols-4 w-full">
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="brand">Brand</TabsTrigger>
                    <TabsTrigger value="prospecting">Prosp.</TabsTrigger>
                    <TabsTrigger value="competitor">Comp.</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <Separator />

              {/* Individual Campaigns List */}
              <div className="max-h-[200px] overflow-y-auto p-1">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center space-x-2 px-2 py-1.5 hover:bg-gray-100 rounded-sm cursor-pointer"
                  >
                    <Checkbox id={`campaign-${campaign.id}`} />
                    <label htmlFor={`campaign-${campaign.id}`} className="text-sm truncate cursor-pointer">
                      {campaign.name}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Metrics Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 text-gray-900">
                Metrics ({settings.metrics.length})
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-2">
              <div className="flex">
                {/* Column 1 - Volume metrics */}
                <div className="w-36 border-r border-gray-200 p-2">
                  <p className="text-xs text-gray-500 px-2 py-1">Volume</p>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="metric-spend"
                      checked={settings.metrics.includes('spend')}
                      onCheckedChange={() => handleMetricToggle('spend')}
                    />
                    <label htmlFor="metric-spend" className="text-sm cursor-pointer">Spend</label>
                  </div>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="metric-impressions"
                      checked={settings.metrics.includes('impressions')}
                      onCheckedChange={() => handleMetricToggle('impressions')}
                    />
                    <label htmlFor="metric-impressions" className="text-sm cursor-pointer">Impressions</label>
                  </div>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="metric-clicks"
                      checked={settings.metrics.includes('clicks')}
                      onCheckedChange={() => handleMetricToggle('clicks')}
                    />
                    <label htmlFor="metric-clicks" className="text-sm cursor-pointer">Clicks</label>
                  </div>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="metric-conversions"
                      checked={settings.metrics.includes('conversions')}
                      onCheckedChange={() => handleMetricToggle('conversions')}
                    />
                    <label htmlFor="metric-conversions" className="text-sm cursor-pointer">Conversions</label>
                  </div>
                </div>

                {/* Column 2 - Efficiency metrics */}
                <div className="w-36 border-r border-gray-200 p-2">
                  <p className="text-xs text-gray-500 px-2 py-1">Efficiency</p>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="metric-cpc"
                      checked={settings.metrics.includes('cpc')}
                      onCheckedChange={() => handleMetricToggle('cpc')}
                    />
                    <label htmlFor="metric-cpc" className="text-sm cursor-pointer">CPC</label>
                  </div>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="metric-cpa"
                      checked={settings.metrics.includes('cpa')}
                      onCheckedChange={() => handleMetricToggle('cpa')}
                    />
                    <label htmlFor="metric-cpa" className="text-sm cursor-pointer">CPA</label>
                  </div>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="metric-roas"
                      checked={settings.metrics.includes('roas')}
                      onCheckedChange={() => handleMetricToggle('roas')}
                    />
                    <label htmlFor="metric-roas" className="text-sm cursor-pointer">ROAS</label>
                  </div>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="metric-conversionValue"
                      checked={settings.metrics.includes('conversionValue')}
                      onCheckedChange={() => handleMetricToggle('conversionValue')}
                    />
                    <label htmlFor="metric-conversionValue" className="text-sm cursor-pointer">Conv. Value</label>
                  </div>
                </div>

                {/* Column 3 - Overlays */}
                <div className="w-44 p-2">
                  <p className="text-xs text-gray-500 px-2 py-1">Overlays</p>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="overlay-newCampaigns"
                      checked={settings.overlays.newCampaigns}
                      onCheckedChange={(checked) =>
                        setSettings((prev) => ({ ...prev, overlays: { ...prev.overlays, newCampaigns: checked === true } }))
                      }
                    />
                    <label htmlFor="overlay-newCampaigns" className="text-sm cursor-pointer">New Campaigns</label>
                  </div>
                  <div className="flex items-center space-x-2 px-2 py-1.5">
                    <Checkbox
                      id="overlay-optimizations"
                      checked={settings.overlays.optimizations}
                      onCheckedChange={(checked) =>
                        setSettings((prev) => ({ ...prev, overlays: { ...prev.overlays, optimizations: checked === true } }))
                      }
                    />
                    <label htmlFor="overlay-optimizations" className="text-sm cursor-pointer">Optimizations</label>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Date Range & Grouping Combined Selector */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 text-gray-900">
                {settings.dateRange === -1 && settings.customDateStart && settings.customDateEnd
                  ? `${new Date(settings.customDateStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${new Date(settings.customDateEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                  : DATE_RANGE_OPTIONS.find((d) => d.value === settings.dateRange)?.label}
                , {GROUP_BY_OPTIONS.find((g) => g.value === settings.groupBy)?.label}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              <div className="flex">
                {/* Left side - Custom Date Picker */}
                <div className="w-44 border-r border-gray-200 p-3">
                  <Label className="text-xs text-gray-500 mb-2 block">Custom Range</Label>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-gray-700">Start</Label>
                      <Input
                        type="date"
                        className="h-8 text-sm"
                        value={settings.customDateStart || ''}
                        onChange={(e) => {
                          setSettings((prev) => ({
                            ...prev,
                            customDateStart: e.target.value,
                            dateRange: -1,
                          }));
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-700">End</Label>
                      <Input
                        type="date"
                        className="h-8 text-sm"
                        value={settings.customDateEnd || ''}
                        onChange={(e) => {
                          setSettings((prev) => ({
                            ...prev,
                            customDateEnd: e.target.value,
                            dateRange: -1,
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Middle - Date Range Presets */}
                <div className="w-36 border-r border-gray-200 p-2">
                  <p className="text-xs text-gray-500 px-2 py-1">Date Range</p>
                  {DATE_RANGE_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center space-x-2 px-2 py-1.5 hover:bg-gray-100 rounded-sm cursor-pointer"
                      onClick={() => setSettings((prev) => ({ ...prev, dateRange: option.value, customDateStart: null, customDateEnd: null }))}
                    >
                      <Checkbox
                        checked={settings.dateRange === option.value}
                        className="pointer-events-none"
                      />
                      <span className="text-sm">{option.label}</span>
                    </div>
                  ))}
                </div>

                {/* Right side - Grouping */}
                <div className="w-32 p-2">
                  <p className="text-xs text-gray-500 px-2 py-1">Group By</p>
                  {GROUP_BY_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center space-x-2 px-2 py-1.5 hover:bg-gray-100 rounded-sm cursor-pointer"
                      onClick={() => setSettings((prev) => ({ ...prev, groupBy: option.value as ChartSettings['groupBy'] }))}
                    >
                      <Checkbox
                        checked={settings.groupBy === option.value}
                        className="pointer-events-none"
                      />
                      <span className="text-sm">{option.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* More Options Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2" align="end">
              <p className="text-xs text-gray-500 px-2 py-1">Chart Type</p>
              {CHART_TYPE_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center space-x-2 px-2 py-1.5 hover:bg-gray-100 rounded-sm cursor-pointer"
                  onClick={() => setSettings((prev) => ({ ...prev, chartType: option.value as ChartSettings['chartType'] }))}
                >
                  <Checkbox
                    checked={settings.chartType === option.value}
                    className="pointer-events-none"
                  />
                  <span className="text-sm">{option.label}</span>
                </div>
              ))}
            </PopoverContent>
          </Popover>

      {/* Pin Button */}
      <Button
        variant={isPinned ? 'default' : 'outline'}
        size="icon"
        className={cn('h-9 w-9', isPinned && 'bg-[#290BD9] hover:bg-[#2309C5]')}
        onClick={() => setIsPinned(!isPinned)}
      >
        <Pin className={cn('h-4 w-4', isPinned && 'text-white')} />
      </Button>
    </>
  );

  // Pass controls to parent if callback provided
  useEffect(() => {
    if (onControlsReady) {
      onControlsReady(controlsElement);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onControlsReady, settings, isPinned, campaigns]);

  return (
    <>
      {/* Header with controls - only show if onControlsReady not provided */}
      {!onControlsReady && (
        <div className="flex items-center justify-end pb-4">
          <div className="flex items-center gap-2">
            {controlsElement}
          </div>
        </div>
      )}

      {/* Chart Area */}
      <div>{renderChart()}</div>

      {/* Active filters summary */}
      <div className="pt-4 flex flex-wrap gap-2">
        <span className="text-xs text-gray-500">
          Showing: {CAMPAIGN_FILTER_OPTIONS.find((c) => c.value === settings.campaigns)?.label} •{' '}
          {settings.metrics.map((m) => METRIC_OPTIONS.find((o) => o.value === m)?.label).join(', ')} •{' '}
          {CHART_TYPE_OPTIONS.find((c) => c.value === settings.chartType)?.label}
        </span>
      </div>
    </>
  );
}
